import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button, IconButton, Toolbar, Menu, MenuItem, Box, Tabs, Tab } from '@material-ui/core';
import i18n from 'i18n-js';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { useStyles } from './styles';
import globalStore from 'store/globalStore';
import agent from 'api/agent';
import PeriodClosingsTab from './components/PeriodClosingsTab';
import MonthlyClosingsTab from './components/MonthlyClosingsTab';

export interface ClosingReport {
    id: string;
    dateFrom: number;
    dateTo: number;
    totalAmount: number;
    totalTax: number;
    type: ClosingReportType;
}

export enum ClosingReportType {
    X = 'X',
    Z = 'Z',
}

export enum ClosingVariants {
    Periodic = 'PERIODIC',
    Monthly = 'MONTHLY',
}

export default function Closings() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const [tabActive, setTabActive] = useState(ClosingVariants.Periodic);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    function handleChangeTab(_event: React.ChangeEvent<{}>, view: ClosingVariants) {
        if (view !== tabActive) {
            setTabActive(view);
        }
    }

    async function createNewReport(type: ClosingReportType) {
        dispatch(globalStore.actionCreators.showSpiner());
        setAnchorEl(null);
        try {
            const newReport = await agent.Payments.CreateNewClosingReport({
                closingType: type,
                variant: tabActive,
            });
            if (newReport) {
                await agent.Payments.GenerateClosingReportPDF(
                    newReport.id,
                    tabActive === ClosingVariants.Periodic
                        ? `daily-closing.pdf`
                        : `monthly-closing.pdf`
                );
                setRefreshKey((k) => k + 1);
            }
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    return (
        <Box className={classes.root}>
            <Toolbar
                style={{
                    paddingBottom: '10px',
                }}
            >
                <Box display="flex" alignItems="center">
                    <IconButton
                        aria-label="delete"
                        size="small"
                        color="primary"
                        onClick={() => history.goBack()}
                    >
                        <ArrowBackIcon fontSize="inherit" />
                    </IconButton>
                    <Tabs
                        value={tabActive}
                        onChange={handleChangeTab}
                        aria-label="wrapped label tabs example"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab
                            value={ClosingVariants.Periodic}
                            label={i18n.t('navigationTabs.periodClosings')}
                        />
                        <Tab
                            value={ClosingVariants.Monthly}
                            label={i18n.t('navigationTabs.monthlyClosings')}
                        />
                    </Tabs>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    style={{ marginRight: 5, width: '212px' }}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                    {i18n.t('common.generateClosingReport').toUpperCase()}
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        style: {
                            minWidth: '212px',
                            marginTop: '40px',
                        },
                    }}
                >
                    {tabActive === ClosingVariants.Periodic && [
                        <MenuItem
                            key="periodic-x"
                            onClick={() => createNewReport(ClosingReportType.X)}
                        >
                            {`X - ${i18n.t('common.periodClosing')}`}
                        </MenuItem>,
                        <MenuItem
                            key="periodic-z"
                            onClick={() => createNewReport(ClosingReportType.Z)}
                        >
                            {`Z - ${i18n.t('common.periodClosing')}`}
                        </MenuItem>,
                    ]}
                    {tabActive === ClosingVariants.Monthly && [
                        <MenuItem
                            key="monthly-x"
                            onClick={() => createNewReport(ClosingReportType.X)}
                        >
                            {`X - ${i18n.t('common.monthlyClosing')}`}
                        </MenuItem>,
                        <MenuItem
                            key="monthly-z"
                            onClick={() => createNewReport(ClosingReportType.Z)}
                        >
                            {`Z - ${i18n.t('common.monthlyClosing')}`}
                        </MenuItem>,
                    ]}
                </Menu>
            </Toolbar>
            {tabActive === ClosingVariants.Periodic && <PeriodClosingsTab key={refreshKey} />}
            {tabActive === ClosingVariants.Monthly && <MonthlyClosingsTab key={refreshKey} />}
        </Box>
    );
}
